(() => {
  if (window.__roadRexInlineFbtLoaded) return;
  window.__roadRexInlineFbtLoaded = true;

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character]);

  const formatMoney = (cents, format) => {
    const value = Number(cents || 0);
    const template = format || '${{amount}}';
    const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match?.[1] || 'amount';
    const decimals = placeholder.includes('no_decimals') ? 0 : 2;
    const decimalMark = placeholder.includes('comma_separator') ? ',' : '.';
    const thousands = placeholder.includes('comma_separator') ? '.' : ',';
    const number = (value / 100).toFixed(decimals);
    const [whole, fraction] = number.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return template.replace(match?.[0] || '{{amount}}', fraction ? `${grouped}${decimalMark}${fraction}` : grouped);
  };

  class RoadRexInlineFbt extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.list = this.querySelector('[data-rr-inline-fbt-list]');
      this.footer = this.querySelector('[data-rr-inline-fbt-footer]');
      this.addButton = this.querySelector('[data-rr-inline-fbt-add]');
      this.totalNode = this.querySelector('[data-rr-inline-fbt-total]');
      this.countNode = this.querySelector('[data-rr-inline-fbt-count]');
      this.statusNode = this.querySelector('[data-rr-inline-fbt-status]');
      this.mainQuantity = this.closest('road-rex-product')?.querySelector('[data-rr-quantity]');
      this.boundChange = (event) => this.handleChange(event);
      this.boundAdd = () => this.handlePrimaryAction();
      this.boundVariantChange = (event) => this.updateMainVariant(event.detail?.variant);
      this.addEventListener('change', this.boundChange);
      this.addButton?.addEventListener('click', this.boundAdd);
      this.mainQuantity?.addEventListener('change', this.boundChange);
      document.addEventListener('rr:variant-change', this.boundVariantChange);

      if (this.dataset.loaded === 'true') {
        this.finishLoading();
      } else {
        this.loadRecommendations();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.removeEventListener('change', this.boundChange);
      this.addButton?.removeEventListener('click', this.boundAdd);
      this.mainQuantity?.removeEventListener('change', this.boundChange);
      document.removeEventListener('rr:variant-change', this.boundVariantChange);
    }

    async requestRecommendations(intent) {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(this.dataset.productId)}&limit=${encodeURIComponent(this.dataset.limit || 3)}&intent=${intent}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: this.controller.signal });
      if (!response.ok) throw new Error(`Recommendations failed (${response.status})`);
      const payload = await response.json();
      return Array.isArray(payload.products) ? payload.products : [];
    }

    async loadRecommendations() {
      this.controller = new AbortController();
      try {
        let products = await this.requestRecommendations('complementary');
        if (!products.length) products = await this.requestRecommendations('related');
        products = products
          .filter((product) => String(product.id) !== String(this.dataset.productId))
          .slice(0, Number(this.dataset.limit || 3));

        if (!products.length) {
          this.hideWhenEmpty();
          return;
        }

        this.querySelector('[data-rr-inline-fbt-loading]')?.remove();
        products.forEach((product) => this.list?.insertAdjacentHTML('beforeend', this.productMarkup(product)));
        this.finishLoading();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.hideWhenEmpty();
      }
    }

    productMarkup(product) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const selected = variants.find((variant) => variant.available) || variants[0] || {};
      const imageCandidate = product.featured_image || product.images?.[0] || '';
      const image = typeof imageCandidate === 'string' ? imageCandidate : (imageCandidate?.url || imageCandidate?.src || '');
      const hasOptions = variants.length > 1 || (selected.title && selected.title !== 'Default Title');
      const variantControl = hasOptions
        ? `<label><span class="visually-hidden">Choose ${escapeHtml(product.title)} option</span><select data-rr-inline-fbt-variant>${variants.map((variant) => `<option value="${escapeHtml(variant.id)}" data-price="${escapeHtml(variant.price)}" data-available="${Boolean(variant.available)}" ${String(variant.id) === String(selected.id) ? 'selected' : ''} ${variant.available ? '' : 'disabled'}>${escapeHtml(variant.title)}</option>`).join('')}</select></label>`
        : `<span>${escapeHtml(selected.title === 'Default Title' ? 'Standard' : (selected.title || 'Standard'))}</span>`;
      const availabilityLabel = selected.available
        ? (this.dataset.availableLabel || 'Available')
        : (this.dataset.unavailableLabel || 'Currently unavailable');

      return `<div class="rr-inline-fbt__item" data-rr-inline-fbt-item>
        <label class="rr-inline-fbt__check"><input type="checkbox" data-rr-inline-fbt-choice data-product-id="${escapeHtml(product.id)}" data-variant-id="${escapeHtml(selected.id)}" data-price="${escapeHtml(selected.price)}" data-available="${Boolean(selected.available)}" data-product-title="${escapeHtml(product.title)}" data-variant-title="${escapeHtml(selected.title)}" data-product-url="${escapeHtml(product.url)}" ${selected.available ? '' : 'disabled'}><span class="visually-hidden">Add ${escapeHtml(product.title)} to package</span></label>
        <div class="rr-inline-fbt__media">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" width="90" height="90" loading="lazy" decoding="async">` : ''}</div>
        <div class="rr-inline-fbt__copy"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a><div class="rr-inline-fbt__variant-row">${variantControl}<b data-rr-inline-fbt-price>${formatMoney(selected.price, this.dataset.moneyFormat)}</b></div><p class="rr-inline-fbt__availability${selected.available ? '' : ' is-unavailable'}" data-rr-inline-fbt-availability>${escapeHtml(availabilityLabel)}</p></div>
      </div>`;
    }

    finishLoading() {
      const extras = this.extraChoices();
      if (!extras.length) {
        this.hideWhenEmpty();
        return;
      }
      this.querySelector('[data-rr-inline-fbt-loading]')?.remove();
      if (this.footer) this.footer.hidden = false;
      this.updateSummary();
    }

    hideWhenEmpty() {
      if (this.dataset.designMode === 'true') {
        const loading = this.querySelector('[data-rr-inline-fbt-loading]');
        if (loading) loading.innerHTML = `<span>${escapeHtml(this.dataset.emptyLabel || 'Add complementary products in Shopify Search & Discovery to show this module.')}</span>`;
        return;
      }
      this.hidden = true;
    }

    choices() {
      return [...this.querySelectorAll('[data-rr-inline-fbt-choice]')];
    }

    extraChoices() {
      return this.choices().filter((choice) => !choice.hasAttribute('data-rr-inline-fbt-main'));
    }

    selectedChoices() {
      return this.choices().filter((choice) => choice.checked && choice.dataset.available !== 'false' && choice.dataset.variantId);
    }

    setAvailability(item, isAvailable) {
      const availability = item?.querySelector('[data-rr-inline-fbt-availability]');
      if (!availability) return;
      availability.textContent = isAvailable
        ? (this.dataset.availableLabel || 'Available')
        : (this.dataset.unavailableLabel || 'Currently unavailable');
      availability.classList.toggle('is-unavailable', !isAvailable);
    }

    handleChange(event) {
      const select = event?.target?.closest?.('[data-rr-inline-fbt-variant]');
      if (select) {
        const item = select.closest('[data-rr-inline-fbt-item]');
        const choice = item?.querySelector('[data-rr-inline-fbt-choice]');
        const option = select.selectedOptions?.[0];
        if (choice && option) {
          choice.dataset.variantId = option.value;
          choice.dataset.price = option.dataset.price || '0';
          choice.dataset.available = option.dataset.available || 'false';
          choice.dataset.variantTitle = option.textContent?.trim() || '';
          choice.disabled = option.dataset.available !== 'true';
          if (choice.disabled) choice.checked = false;
          this.setAvailability(item, !choice.disabled);
          const price = item.querySelector('[data-rr-inline-fbt-price]');
          if (price) price.textContent = formatMoney(option.dataset.price, this.dataset.moneyFormat);
        }
      }
      this.updateSummary();
    }

    updateMainVariant(variant) {
      const choice = this.querySelector('[data-rr-inline-fbt-main]');
      if (!choice || !variant) return;
      choice.dataset.variantId = String(variant.id || '');
      choice.dataset.price = String(variant.price || 0);
      choice.dataset.available = String(Boolean(variant.available));
      choice.dataset.variantTitle = String(variant.public_title || variant.title || '');
      choice.disabled = !variant.available;
      if (choice.disabled) choice.checked = false;
      this.setAvailability(choice.closest('[data-rr-inline-fbt-item]'), Boolean(variant.available));
      const price = this.querySelector('[data-rr-inline-fbt-main-price]');
      if (price) price.textContent = formatMoney(variant.price, this.dataset.moneyFormat);
      this.updateSummary();
    }

    updateSummary() {
      const selected = this.selectedChoices();
      const mainQuantity = Math.max(1, Number(this.mainQuantity?.value) || 1);
      const total = selected.reduce((sum, choice) => sum + (Number(choice.dataset.price || 0) * (choice.hasAttribute('data-rr-inline-fbt-main') ? mainQuantity : 1)), 0);
      if (this.totalNode) this.totalNode.textContent = formatMoney(total, this.dataset.moneyFormat);
      if (this.countNode) this.countNode.textContent = `${selected.length} ${selected.length === 1 ? 'product' : 'products'}`;
      if (this.addButton) this.addButton.disabled = selected.length < 1;
    }

    mainProperties() {
      const properties = { '_Road Rex 4x4 package': 'Complete the setup' };
      this.closest('form')?.querySelectorAll('input[name^="properties["]:not([disabled])').forEach((input) => {
        const key = input.name.match(/^properties\[(.+)\]$/)?.[1];
        if (key && input.value) properties[key] = input.value;
      });
      return properties;
    }

    buildItems() {
      const quantity = Math.max(1, Number(this.mainQuantity?.value) || 1);
      return this.selectedChoices().map((choice) => {
        const isMain = choice.hasAttribute('data-rr-inline-fbt-main');
        return {
          id: Number(choice.dataset.variantId),
          quantity: isMain ? quantity : 1,
          properties: isMain ? this.mainProperties() : { '_Road Rex 4x4 package': 'Companion product — confirm fitment' },
        };
      });
    }

    handlePrimaryAction() {
      if (this.dataset.action !== 'enquiry') {
        this.addSelected();
        return;
      }
      if (!this.addButton) return;
      const selected = this.selectedChoices();
      if (!selected.length) return;
      const mainQuantity = Math.max(1, Number(this.mainQuantity?.value) || 1);
      const productSummary = selected.map((choice) => {
        const title = choice.dataset.productTitle || 'Selected product';
        const variant = choice.dataset.variantTitle && choice.dataset.variantTitle !== 'Default Title'
          ? ` — ${choice.dataset.variantTitle}`
          : '';
        const quantity = choice.hasAttribute('data-rr-inline-fbt-main') && mainQuantity > 1
          ? ` × ${mainQuantity}`
          : '';
        return `${title}${variant}${quantity}`;
      }).join(' + ');
      this.addButton.dataset.productTitle = productSummary;
    }

    async addSelected() {
      const items = this.buildItems();
      if (items.length < 1 || this.adding || !this.addButton) return;
      this.adding = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      const label = this.addButton.querySelector('[data-rr-inline-fbt-add-label]');
      if (label) label.textContent = this.addButton.dataset.loadingLabel || 'Adding…';
      this.setStatus('');
      try {
        if (window.RoadRexCart?.add) {
          await window.RoadRexCart.add({ items }, { opener: this.addButton, open: true });
        } else {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || payload.message || `Cart request failed (${response.status})`);
          }
          document.dispatchEvent(new CustomEvent('rr:cart:refresh'));
          document.dispatchEvent(new CustomEvent('rr:cart:open', { detail: { opener: this.addButton } }));
        }
        this.setStatus(this.dataset.successLabel || 'Selected products added to cart.');
      } catch (error) {
        this.setStatus(error.message || this.dataset.errorLabel || 'Selected products could not be added.', true);
      } finally {
        this.adding = false;
        this.addButton.removeAttribute('aria-busy');
        if (label) label.textContent = this.addButton.dataset.defaultLabel || 'Add selected package';
        this.updateSummary();
      }
    }

    setStatus(message, isError = false) {
      if (!this.statusNode) return;
      this.statusNode.textContent = message;
      this.statusNode.classList.toggle('has-error', isError);
    }
  }

  if (!customElements.get('road-rex-inline-fbt')) customElements.define('road-rex-inline-fbt', RoadRexInlineFbt);
})();
